import { FC, useMemo } from 'react';
import { Stack } from '@mui/material';

import { TAnyItineraryView } from '@mopla/data-models';

import { BookingRouteSummary } from './BookingRouteSummary';
import { RouteStop } from './RouteStop';
import { ERouteStopVariant, mapItineraryToRouteStops } from './utils';

interface IBookingRouteCompactProps {
	itinerary: TAnyItineraryView;
}

/**
 * Collapsed route view: departure, arrival stops and transfers/stops in between.
 */
export const BookingRouteCompact: FC<IBookingRouteCompactProps> = ({
	itinerary,
}) => {
	/** Only the outer stops are rendered, so leg prices and labels are irrelevant here */
	const stops = useMemo(() => mapItineraryToRouteStops(itinerary), [itinerary]);

	const firstStop = stops[0];
	const lastStop = stops[stops.length - 1];

	if (!firstStop || stops.length < 2) return null;

	/** Both counts are aggregated by the BE; legacy itineraries carry neither */
	const transfersCount = itinerary.transfers ?? 0;
	const stopsCount = itinerary.stops ?? 0;
	const hasSummary = Boolean(transfersCount || stopsCount);

	return (
		<Stack>
			<RouteStop
				variant={ERouteStopVariant.START}
				time={firstStop.time}
				effectiveTime={firstStop.effectiveTime}
				lat={firstStop.lat}
				lng={firstStop.lng}
				address={firstStop.address}
				dashedConnector={hasSummary}
			>
				{hasSummary && (
					<BookingRouteSummary
						transfersCount={transfersCount}
						stopsCount={stopsCount}
					/>
				)}
			</RouteStop>
			<RouteStop
				variant={ERouteStopVariant.END}
				time={lastStop.time}
				effectiveTime={lastStop.effectiveTime}
				lat={lastStop.lat}
				lng={lastStop.lng}
				address={lastStop.address}
			/>
		</Stack>
	);
};
